import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  obtenerFacturasProveedor,
  crearFacturaProveedor,
  actualizarFacturaProveedor,
  eliminarFacturaProveedor,
} from "../services/proveedoresFacturasService";
import { obtenerProveedores } from "../services/proveedoresService";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";

const emptyForm = {
  idFactura: 0,
  idProveedor: "",
  claveProveedor: "",
  serie: "",
  folioFactura: "",
  fechaFactura: "",
  descripcion: "",
  importe: "",
  aplicaIVA: true,
  iva: "",
  uuid: "",
  estatus: 1,
  observaciones: "",
};

const pickFirst = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
};
const normProv = (p) => ({
  id: pickFirst(p.id, p.Id, p.idProveedor, p.IdProveedor, 0),
  clave: pickFirst(p.clave, p.Clave, ""),
  nombre: pickFirst(p.nombre, p.Nombre, ""),
});

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("es-MX");
  } catch {
    return d;
  }
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const ESTATUS_LABEL = {
  1: "Pendiente",
  2: "Aprobada",
  3: "Rechazada",
  4: "Pagada",
};
const ESTATUS_BADGE = {
  1: "lmd-badge-warning",
  2: "lmd-badge-success",
  3: "lmd-badge-danger",
  4: "lmd-badge-primary",
};

/* ─── Campo reutilizable — definido FUERA del componente padre ────────────
   Esto evita que React lo desmonte/monte en cada render causando pérdida de foco */
const Campo = ({
  label,
  name,
  type = "text",
  disabled = false,
  value,
  onChange,
}) => (
  <div className="lmd-field">
    <label className="lmd-label">{label}</label>
    <input
      className="lmd-input"
      type={type}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
    />
  </div>
);

/* ─── Formulario del modal — también fuera, con su propio estado ────────── */
function FacturaForm({ initialData, proveedores, onGuardar, onCancelar }) {
  const [form, setForm] = useState(initialData);

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const onSelectProv = (clave) => {
    const prov = proveedores.find((p) => p.clave === clave);
    setForm((prev) => ({
      ...prev,
      claveProveedor: clave,
      idProveedor: prov ? prov.id : "",
    }));
  };

  const handleGuardar = () => onGuardar(form);

  return (
    <>
      <div className="lmd-modal-body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--sp-4)",
          }}
        >
          {/* Proveedor */}
          <div className="lmd-field" style={{ gridColumn: "1 / -1" }}>
            <label className="lmd-label">Proveedor *</label>
            <select
              className="lmd-select"
              style={{ width: "100%" }}
              value={form.claveProveedor}
              onChange={(e) => onSelectProv(e.target.value)}
            >
              <option value="">-- Seleccionar proveedor --</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.clave}>
                  {p.clave} — {p.nombre}
                </option>
              ))}
            </select>
          </div>

          <Campo
            label="Serie"
            name="serie"
            value={form.serie}
            onChange={setField}
          />
          <Campo
            label="Folio"
            name="folioFactura"
            value={form.folioFactura}
            onChange={setField}
          />
          <Campo
            label="Fecha"
            name="fechaFactura"
            value={form.fechaFactura}
            onChange={setField}
            type="date"
          />
          <Campo
            label="Descripcion"
            name="descripcion"
            value={form.descripcion}
            onChange={setField}
          />
          <Campo
            label="Importe"
            name="importe"
            value={form.importe}
            onChange={setField}
            type="number"
          />

          <div className="lmd-field">
            <label className="lmd-label">Aplica IVA</label>
            <select
              className="lmd-select"
              style={{ width: "100%" }}
              value={form.aplicaIVA ? "1" : "0"}
              onChange={(e) => setField("aplicaIVA", e.target.value === "1")}
            >
              <option value="1">Si</option>
              <option value="0">No</option>
            </select>
          </div>

          <Campo
            label="IVA"
            name="iva"
            value={form.iva}
            onChange={setField}
            type="number"
            disabled={!form.aplicaIVA}
          />
          <Campo
            label="UUID"
            name="uuid"
            value={form.uuid}
            onChange={setField}
          />

          <div className="lmd-field">
            <label className="lmd-label">Estatus</label>
            <select
              className="lmd-select"
              style={{ width: "100%" }}
              value={form.estatus}
              onChange={(e) => setField("estatus", Number(e.target.value))}
            >
              <option value={1}>Pendiente</option>
              <option value={2}>Aprobada</option>
              <option value={3}>Rechazada</option>
              <option value={4}>Pagada</option>
            </select>
          </div>

          <Campo
            label="Observaciones"
            name="observaciones"
            value={form.observaciones}
            onChange={setField}
          />
        </div>
      </div>

      <div className="lmd-modal-footer">
        <button className="lmd-btn lmd-btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
        <button className="lmd-btn lmd-btn-primary" onClick={handleGuardar}>
          Guardar
        </button>
      </div>
    </>
  );
}

/* ─── Componente principal ────────────────────────────────────────────── */
export default function ProveedoresFacturasPage() {
  const [proveedores, setProveedores] = useState([]);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estatus, setEstatus] = useState("");
  const [idProveedor, setIdProveedor] = useState("");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formInicial, setFormInicial] = useState(emptyForm);

  const cargarProveedores = async () => {
    try {
      const resp = await obtenerProveedores();
      setProveedores((resp.data || []).map(normProv));
    } catch (e) {
      console.error(e);
    }
  };

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await obtenerFacturasProveedor({
        pageNumber,
        pageSize,
        search,
        estatus,
        idProveedor,
        anio,
        mes,
      });
      const payload = resp.data;
      setData(payload.data || []);
      setMeta({
        total: payload.total || 0,
        totalPages: payload.totalPages || 0,
      });
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudieron cargar facturas", "error");
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, search, estatus, idProveedor, anio, mes]);

  useEffect(() => {
    cargarProveedores();
  }, []);
  useEffect(() => {
    cargar();
  }, [cargar]);

  const proveedorPorId = useMemo(() => {
    const map = new Map();
    proveedores.forEach((p) => map.set(Number(p.id), p));
    return map;
  }, [proveedores]);

  const totalImporte = useMemo(
    () => data.reduce((acc, f) => acc + Number(f.importe || 0), 0),
    [data],
  );
  const totalIva = useMemo(
    () => data.reduce((acc, f) => acc + Number(f.iva || 0), 0),
    [data],
  );
  const totalGeneral = totalImporte + totalIva;
  const totalPages = meta.totalPages || 0;

  const abrirNuevo = () => {
    setEditMode(false);
    setFormInicial(emptyForm);
    setModalOpen(true);
  };
  const abrirEditar = (row) => {
    setEditMode(true);
    setFormInicial({
      ...emptyForm,
      ...row,
      fechaFactura: row.fechaFactura?.substring(0, 10) || "",
    });
    setModalOpen(true);
  };

  const guardar = async (form) => {
    if (!form.idProveedor || !form.claveProveedor) {
      Swal.fire(
        "Falta proveedor",
        "Selecciona una clave de proveedor",
        "warning",
      );
      return;
    }
    try {
      const payload = {
        ...form,
        idProveedor: Number(form.idProveedor),
        claveProveedor: String(form.claveProveedor),
        importe: Number(form.importe || 0),
        iva: Number(form.iva || 0),
        estatus: Number(form.estatus || 1),
      };
      if (!payload.aplicaIVA) payload.iva = 0;
      if (!editMode) {
        await crearFacturaProveedor(payload);
      } else {
        await actualizarFacturaProveedor(payload.idFactura, payload);
      }
      Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 1600,
        showConfirmButton: false,
      });
      setModalOpen(false);
      cargar();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo guardar", "error");
    }
  };

  const borrar = async (idFactura) => {
    const r = await Swal.fire({
      icon: "warning",
      title: "Eliminar factura",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    });
    if (!r.isConfirmed) return;
    try {
      await eliminarFacturaProveedor(idFactura);
      Swal.fire({
        icon: "success",
        title: "Eliminada",
        timer: 1400,
        showConfirmButton: false,
      });
      cargar();
    } catch {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  };

  const columns = [
    { key: "idFactura", title: "ID", style: { width: 70 } },
    {
      key: "proveedor",
      title: "Proveedor",
      render: (r) => {
        const p = proveedorPorId.get(Number(r.idProveedor));
        const clave = r.claveProveedor || p?.clave || "—";
        const nombre = r.nombreProveedor || p?.nombre || "";
        return (
          <span title={nombre}>
            <strong>{clave}</strong>
            {nombre ? ` — ${nombre}` : ""}
          </span>
        );
      },
    },
    { key: "serie", title: "Serie", style: { width: 80 } },
    { key: "folioFactura", title: "Folio", style: { width: 100 } },
    {
      key: "fechaFactura",
      title: "Fecha",
      style: { width: 110 },
      render: (r) => fmtDate(r.fechaFactura),
    },
    { key: "descripcion", title: "Descripcion" },
    {
      key: "importe",
      title: "Importe",
      style: { width: 120 },
      className: "num",
      render: (r) => fmt(r.importe),
    },
    {
      key: "iva",
      title: "IVA",
      style: { width: 110 },
      className: "num",
      render: (r) => fmt(r.iva),
    },
    {
      key: "total",
      title: "Total",
      style: { width: 120 },
      className: "num",
      render: (r) => (
        <strong>{fmt(Number(r.importe || 0) + Number(r.iva || 0))}</strong>
      ),
    },
    {
      key: "estatus",
      title: "Estatus",
      style: { width: 110 },
      render: (r) => (
        <span
          className={`lmd-badge ${ESTATUS_BADGE[r.estatus] || "lmd-badge-neutral"}`}
        >
          {ESTATUS_LABEL[r.estatus] || r.estatus}
        </span>
      ),
    },
    {
      key: "acciones",
      title: "",
      style: { width: 120 },
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="lmd-btn lmd-btn-warning lmd-btn-sm"
            onClick={() => abrirEditar(r)}
          >
            Editar
          </button>
          <button
            className="lmd-btn lmd-btn-danger  lmd-btn-sm"
            onClick={() => borrar(r.idFactura)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  const anioActual = new Date().getFullYear();
  const aniosDisponibles = Array.from({ length: 5 }, (_, i) => anioActual - i);

  const extraRight = (
    <>
      <select
        className="lmd-select"
        value={mes}
        onChange={(e) => {
          setMes(Number(e.target.value));
          setPageNumber(1);
        }}
      >
        <option value={0}>Todos</option>
        {MESES.map((nombre, i) => (
          <option key={i + 1} value={i + 1}>
            {nombre}
          </option>
        ))}
      </select>
      <select
        className="lmd-select"
        value={anio}
        onChange={(e) => {
          setAnio(Number(e.target.value));
          setPageNumber(1);
        }}
      >
        {(aniosDisponibles.includes(anio) ? aniosDisponibles : [anio, ...aniosDisponibles]).map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <select
        className="lmd-select"
        value={estatus}
        onChange={(e) => {
          setEstatus(e.target.value);
          setPageNumber(1);
        }}
      >
        <option value="">Todos</option>
        <option value="1">Pendiente</option>
        <option value="2">Aprobada</option>
        <option value="3">Rechazada</option>
        <option value="4">Pagada</option>
      </select>
      <select
        className="lmd-select"
        value={idProveedor}
        onChange={(e) => {
          setIdProveedor(e.target.value);
          setPageNumber(1);
        }}
      >
        <option value="">Todos los proveedores</option>
        {proveedores.map((p) => (
          <option key={p.id} value={p.id}>
            {p.clave} — {p.nombre}
          </option>
        ))}
      </select>
    </>
  );

  return (
    <>
      <PageHeader
        title="Facturas de Proveedores"
        subtitle="Registro y seguimiento de facturas recibidas"
      />

      {/* Resumen */}
      <div
        className="lmd-stats-grid"
        style={{
          marginBottom: "var(--sp-4)",
          gridTemplateColumns: "repeat(3,1fr)",
        }}
      >
        <div className="lmd-stat-card">
          <span className="lmd-stat-label">Registros (pagina)</span>
          <span
            className="lmd-stat-value"
            style={{ fontSize: "var(--fs-2xl)" }}
          >
            {data.length}
          </span>
        </div>
        <div className="lmd-stat-card">
          <span className="lmd-stat-label">Importe + IVA</span>
          <span className="lmd-stat-value" style={{ fontSize: "var(--fs-xl)" }}>
            ${fmt(totalGeneral)}
          </span>
        </div>
        <div className="lmd-stat-card accent">
          <span className="lmd-stat-label">Total registros</span>
          <span
            className="lmd-stat-value"
            style={{ fontSize: "var(--fs-2xl)" }}
          >
            {meta.total}
          </span>
        </div>
      </div>

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por serie, folio, descripcion..."
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPageNumber(1);
          }}
          onSearch={() => setPageNumber(1)}
          sizeValue={pageSize}
          onSizeChange={(n) => {
            setPageSize(n);
            setPageNumber(1);
          }}
          onAdd={abrirNuevo}
          addText="Registrar Factura"
          extraRight={extraRight}
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" />
            Cargando...
          </div>
        )}

        <DataTable columns={columns} rows={data} keyField="idFactura" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{meta.total}</strong> facturas
          </span>
          <div className="lmd-pagination-spacer" />
          <button
            className="lmd-page-btn"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber(1)}
          >
            «
          </button>
          <button
            className="lmd-page-btn"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            ‹
          </button>
          <span
            style={{
              padding: "0 10px",
              fontSize: "var(--fs-sm)",
              color: "var(--clr-text-secondary)",
            }}
          >
            Pag. <strong>{pageNumber}</strong> / <strong>{totalPages}</strong>
          </span>
          <button
            className="lmd-page-btn"
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            ›
          </button>
          <button
            className="lmd-page-btn"
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber(totalPages)}
          >
            »
          </button>
        </div>
      </div>

      {/* Modal — usa key para reiniciar estado interno al abrir */}
      {modalOpen && (
        <div className="lmd-modal-overlay" onClick={() => setModalOpen(false)}>
          <div
            className="lmd-modal lmd-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">
                {editMode ? "Editar Factura" : "Nueva Factura de Proveedor"}
              </h3>
              <button
                className="lmd-modal-close"
                onClick={() => setModalOpen(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <FacturaForm
              key={formInicial.idFactura}
              initialData={formInicial}
              proveedores={proveedores}
              onGuardar={guardar}
              onCancelar={() => setModalOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
